import { useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../../assets/styles/components/Wishtlist/WishlistModal.css";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWishlist, removeFromWishlist, moveToCart } from "./WishlistSlice";
import imgbag from "../../assets/images/empty_bag.png";
import ArrowLeft from "../../assets/icons/ArrowLeft.png";
import { useCartWishlist } from "../../app/CartWishlistContext";
import { Link } from "react-router-dom";
import { getProductPdpUrl } from "../../utils/url.utils";
import { formatDiscount, formatPrice } from "../../utils/price.utils";
import { LazyLoadImage } from "react-lazy-load-image-component";

const WishlistModal = ({ onClose }) => {
  const { getCartWishlistCount } = useCartWishlist();
  const dispatch = useDispatch();
  const { items, loading } = useSelector((state) => state.wishlist);

  useEffect(() => {
    dispatch(fetchWishlist());
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [dispatch]);

  const handleRemove = (wishlistId) => {
    dispatch(removeFromWishlist(wishlistId))
      .unwrap()
      .then(() => {
        getCartWishlistCount(); // refresh count after add
        dispatch(fetchWishlist());
        getCartWishlistCount();
      });
  };

  const handleMoveToCart = (productId, wishlistId) => {
    dispatch(moveToCart({ product_id: productId, quantity: 1 }));
    dispatch(removeFromWishlist(wishlistId))
      .unwrap()
      .then(() => {
        dispatch(fetchWishlist());
        getCartWishlistCount();
      });
  };

  return (
    <div className="wishlist-modal-overlay" onClick={onClose}>
      <div className="wishlist-modal">

        <div className="wishlist-header">
          <h3>
            Wishlist{" "}
            {!loading && (
              <span className="wishlist-header-count">({items.length})</span>
            )}
            {loading && <Skeleton width={30} height={20} />}
          </h3>
          <X onClick={onClose} className="close-icon-whst" />
        </div>

        <div className="wishlist-content">
          {/* Skeleton loader */}
          {loading &&
            Array(4)
              .fill(0)
              .map((_, idx) => (
                <div
                  className="wishlist-item"
                  key={idx}
                  style={{
                    display: "flex",
                    gap: "12px",
                    padding: "12px 0",
                    alignItems: "center",
                    borderBottom: "1px solid #e5e5e5",
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    <Skeleton
                      width={80}
                      height={80}
                      borderRadius={4}
                      baseColor="#f0f0f0"
                      highlightColor="#e6e6e6"
                    />
                  </div>
                  <div
                    className="wishlist-details"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: "80px",
                    }}
                  >
                    <div>
                      <Skeleton
                        width={150}
                        height={18}
                        style={{ marginBottom: "6px" }}
                        baseColor="#f0f0f0"
                        highlightColor="#e6e6e6"
                      />
                      <Skeleton
                        width={60}
                        height={16}
                        baseColor="#f0f0f0"
                        highlightColor="#e6e6e6"
                      />
                    </div>
                    <div
                      className="wishlist-actions"
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "8px",
                      }}
                    >
                      <Skeleton
                        width={100}
                        height={30}
                        borderRadius={4}
                        baseColor="#f0f0f0"
                        highlightColor="#e6e6e6"
                      />
                      <Skeleton
                        width={80}
                        height={30}
                        borderRadius={4}
                        baseColor="#f0f0f0"
                        highlightColor="#e6e6e6"
                      />
                    </div>
                  </div>
                </div>
              ))}

          {/* Empty wishlist */}
          {!loading && items.length === 0 && (
            <div className="box_wishlist">

              <div className="track_fest_box_image">
                <LazyLoadImage effect="blur" src={imgbag} alt="bags" />
              </div>

              <div className="text_sections">
                <h2 className="txt_title_one">Show your list some love!</h2>
                <p className="sub_title_whistlist">
                  Love something? Hit the heart to add it to your favourites.
                </p>
                <Link to={`/shop/bestsellers`}>
                  <button className="empty-wishlist-btn">
                    Explore Bestsellers &nbsp; <LazyLoadImage effect="blur" src={ArrowLeft} alt="left" height={16} width={16} />
                  </button>
                </Link>
              </div>
            </div>
          )}

          {/* Wishlist items */}
          {!loading &&
            items.map((wishlistItem, index) => {
              const product = wishlistItem.product;
              if (!product) return null;
              return (
                <div
                  className="wishlist-item"
                  key={index}
                  style={{
                    borderBottom: "1px solid #e5e5e5",
                    padding: "12px 0",
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <Link to={getProductPdpUrl(product.action_url, product.sku)} target="_blank" rel="noopener noreferrer">
                    <LazyLoadImage
                      src={product.media}
                      alt={product.name}
                      effect="blur"
                      style={{ width: 110, height: 119, objectFit: "cover" }}
                    />
                  </Link>
                  <div className="wishlist-details" style={{ flex: 1 }}>
                    <div>
                      <Link to={getProductPdpUrl(product.action_url, product.sku)} target="_blank" rel="noopener noreferrer">
                        <p>
                          {product.name.length > 40
                            ? product.name.substring(0, 40) + "..."
                            : product.name}
                        </p>
                      </Link>
                      <span>{formatPrice(product.selling_price)}</span>
                      &nbsp;
                      <span>
                        {product.discount > 0 && (
                          <>
                            <span><del>{formatPrice(product?.mrp)}</del></span> &nbsp;
                            <span className="discount">({formatDiscount(product.discount)})</span>
                          </>
                        )}
                      </span>
                    </div>
                    <div
                      className="wishlist-actions"
                      style={{ marginTop: "8px" }}>
                      <button
                        className="move-to-cart"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveToCart(product.id, wishlistItem.id);
                        }
                        }>
                        MOVE TO CART
                      </button>
                      <button
                        className="move-to-cart"
                        onClick={(e) => { e.stopPropagation(); handleRemove(wishlistItem.id); }}
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default WishlistModal;
